import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { theme } from './theme';
import { store } from './store';
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import Login from './pages/Login';
import IndividualLogin from './pages/IndividualLogin';
import AdminLogin from './pages/AdminLogin';
import TwoFactorPage from './pages/TwoFactorPage';
import AgencyLogin from './pages/AgencyLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminAgencies from './pages/AdminAgencies';
import { useAuth } from './hooks/useAuth';
import Index from './pages/Index';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import AgentSupport from './pages/AgentSupport';
import DeleteAccount from './pages/DeleteAccount';
import ChatBox from './components/ChatBox';
import AgencyRegister from './pages/AgencyRegister';
import Plans from './pages/Plans';
import SetPassword from './pages/SetPassword';
import AdminSettings from './pages/AdminSettings';
import AdminServiceLimitsFees from './pages/AdminServiceLimitsFees';
import AdminPlans from './pages/AdminPlans';
import AdminContractors from './pages/AdminContractors';
import AdminChat from './pages/AdminChat';
import AdminSupportTickets from './pages/AdminSupportTickets';
import AdminUsageTips from './pages/AdminUsageTips';
import AdminDomainIntegration from './pages/AdminDomainIntegration';
import AdminCampaignCredits from './pages/AdminCampaignCredits';
import AdminQueueManagement from './pages/AdminQueueManagement';
import Properties from './pages/Properties';
import PropertyDetails from './pages/PropertyDetails';
import ConsultationReportEdit from './pages/ConsultationReportEdit';
import ConsultationReportPreview from './pages/ConsultationReportPreview';
import ConsultationReportPreviewEmbed from './pages/ConsultationReportPreviewEmbed';
import VisitorInspectionReportEdit from './pages/VisitorInspectionReportEdit';
import Workflows from './pages/Workflows';
import WorkflowTemplates from './pages/WorkflowTemplates';
import CommissionSharingPolicy from './pages/CommissionSharingPolicy';
import CommissionPerformance from './pages/CommissionPerformance';
import CommissionReport from './pages/CommissionReport';
import CostItems from './pages/CostItems';
import Team from './pages/Team';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import ThirdPartyIntegrations from './pages/ThirdPartyIntegrations';
import RealestateIntegration from './pages/RealestateIntegration';
import DomainIntegration from './pages/DomainIntegration';
import StylingCompanies from './pages/StylingCompanies';
import NewProperty from './pages/NewProperty';
import Profile from './pages/Profile';
import Customers from './pages/Customers';
import Attendees from './pages/Attendees';
import Calendar from './pages/Calendar';
import ManageSubscription from './pages/ManageSubscription';
import MarketCampaign from './pages/MarketCampaign';
import ContactSales from './pages/ContactSales';
import Photographers from './pages/Photographers';
import Contractors from './pages/Contractors';
import OpenSchedule from './pages/OpenSchedule';
import AuctionSchedulingPage from './pages/AuctionScheduling';
import PropertyContractorJobsPage from './pages/PropertyContractorJobsPage';
import PropertyRentalApplications from './pages/PropertyRentalApplications';
import RentalApply from './pages/RentalApply';
import RentalApplicationView from './pages/RentalApplicationView';
import ContractorJobPortal from './pages/ContractorJobPortal';
import ContractorRetrieveCode from './pages/ContractorRetrieveCode';
import ContractorDashboard from './pages/ContractorDashboard';
import ContractorLayout from './layouts/ContractorLayout';
import ContractorProfile from './pages/ContractorProfile';
import ContractorAuth from './pages/ContractorAuth';
import { ContractorSubscriptionManagement } from './pages/ContractorSubscriptionManagement';
import VendorReport from './pages/VendorReport';
import VendorReportCode from './pages/VendorReportCode';
import VendorVisitorInspectionReport from './pages/VendorVisitorInspectionReport';
import VendorAgreement from './pages/VendorAgreement';
import { debugSocketIO } from './utils/debugSocketIO';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { hasAdminAccess } from './utils/roleUtils';
import SelfCheckin from './pages/SelfCheckin';
import DemoVideos from './pages/DemoVideos';
import SeoLandingPageRoute from './pages/SeoLandingPageRoute';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

const PrivateRoute = ({
  children,
  requireAdmin = false,
  requireAgencyAdmin = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireAgencyAdmin?: boolean;
}) => {
  const { user, loading, token, isAuthenticated } = useAuth();

  console.log('PrivateRoute - Auth state:', { user: !!user, loading, token: !!token, isAuthenticated });

  // Show loading if we're checking authentication (have token but no user yet)
  if (loading || (token && !user)) {
    console.log('PrivateRoute - Showing loading state');
    return <div>Loading...</div>;
  }

  // If no user and not authenticated, redirect to login
  if (!user || !isAuthenticated) {
    console.log('PrivateRoute - No user or not authenticated, redirecting to login');
    return <Navigate to={requireAdmin ? '/admin/login' : '/login'} replace />;
  }

  // Check admin role if required
  if (requireAdmin && user.primaryRole !== 'app_admin') {
    console.log('PrivateRoute - User is not admin, redirecting to login');
    console.log('PrivateRoute - User role:', user.primaryRole);
    console.log('PrivateRoute - Role type:', typeof user.primaryRole);
    console.log('PrivateRoute - Role comparison:', user.primaryRole === 'app_admin');
    console.log('PrivateRoute - User object:', user);
    return <Navigate to="/login" replace />;
  }

  // Check agency admin role if required (agency admins or app admins allowed)
  if (requireAgencyAdmin && !hasAdminAccess(user)) {
    console.log('PrivateRoute - User is not agency admin, redirecting');
    return <Navigate to="/login" replace />;
  }

  console.log('PrivateRoute - Rendering protected content');
  return <>{children}</>;
};

// Wrapper component to handle agencySlug parameter
const WorkflowTemplatesWrapper = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  return <WorkflowTemplates agencySlug={agencySlug || ''} />;
};

const App = () => {
  // Make debug function available in browser console
  React.useEffect(() => {
    (window as any).debugSocketIO = debugSocketIO;
    console.log('🔧 Socket.IO debug function available as window.debugSocketIO()');
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/" element={<><Index /><ChatBox /></>} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/support" element={<Support />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/demo" element={<DemoVideos />} />
            <Route path="/register" element={<AgencyRegister />} />
            <Route path="/login" element={<Login />} />
            <Route path="/two-factor" element={<TwoFactorPage />} />
            <Route path="/individual/login" element={<IndividualLogin />} />
            <Route path="/individual" element={<Navigate to="/individual/login" replace />} />
            <Route path="/checkin" element={<SelfCheckin />} />
            <Route path="/seo/:seoSlug" element={<SeoLandingPageRoute />} />
            <Route path="/real-estate-crm-australia" element={<SeoLandingPageRoute />} />
            <Route path="/real-estate-crm-sydney" element={<SeoLandingPageRoute />} />
            <Route path="/crm-for-real-estate-agencies" element={<SeoLandingPageRoute />} />
            <Route path="/open-house-management-software" element={<SeoLandingPageRoute />} />
            <Route path="/workflow-estate-vs-agentbox" element={<SeoLandingPageRoute />} />
            <Route path="/vendor/report" element={<VendorReportCode />} />
            <Route path="/vendor/report/code/:code" element={<VendorReport />} />
            <Route path="/vendor/report/:token" element={<VendorReport />} />
            <Route path="/vendor/visitor-inspection-report" element={<VendorVisitorInspectionReport />} />
            <Route path="/vendor/visitor-inspection-report/code/:code" element={<VendorVisitorInspectionReport />} />
            <Route path="/vendor/visitor-inspection-report/:token" element={<VendorVisitorInspectionReport />} />
            <Route path="/:agencySlug/vendor/visitor-inspection-report" element={<VendorVisitorInspectionReport />} />
            <Route path="/:agencySlug/vendor/visitor-inspection-report/code/:code" element={<VendorVisitorInspectionReport />} />
            <Route path="/:agencySlug/vendor/visitor-inspection-report/:token" element={<VendorVisitorInspectionReport />} />
            <Route path="/vendor/agreement/code/:code" element={<VendorAgreement />} />
            <Route path="/vendor/agreement/:token" element={<VendorAgreement />} />
            <Route path="/vendor/rental-agreement/code/:code" element={<VendorAgreement agreementType="rental" />} />
            <Route path="/vendor/rental-agreement/:token" element={<VendorAgreement agreementType="rental" />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/:agencySlug/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={<AdminLayout><Outlet /></AdminLayout>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<PrivateRoute requireAdmin={true}><AdminDashboard /></PrivateRoute>} />
              <Route path="agencies" element={<PrivateRoute requireAdmin={true}><AdminAgencies /></PrivateRoute>} />
              <Route path="contractors" element={<PrivateRoute requireAdmin={true}><AdminContractors /></PrivateRoute>} />
              <Route path="plans" element={<PrivateRoute requireAdmin={true}><AdminPlans /></PrivateRoute>} />
              <Route path="campaign-credits" element={<PrivateRoute requireAdmin={true}><AdminCampaignCredits /></PrivateRoute>} />
              <Route path="service-limits-fees" element={<PrivateRoute requireAdmin={true}><AdminServiceLimitsFees /></PrivateRoute>} />
              <Route path="settings" element={<PrivateRoute requireAdmin={true}><AdminSettings /></PrivateRoute>} />
              <Route path="domain-integration" element={<PrivateRoute requireAdmin={true}><AdminDomainIntegration /></PrivateRoute>} />
              <Route path="queue-management" element={<PrivateRoute requireAdmin={true}><AdminQueueManagement /></PrivateRoute>} />
              <Route path="chat" element={<PrivateRoute requireAdmin={true}><AdminChat /></PrivateRoute>} />
              <Route path="support-tickets" element={<PrivateRoute requireAdmin={true}><AdminSupportTickets /></PrivateRoute>} />
              <Route path="usage-tips" element={<PrivateRoute requireAdmin={true}><AdminUsageTips /></PrivateRoute>} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Route>
            <Route path="/agencies/:agencySlug/dashboard" element={<PrivateRoute><MainLayout><Dashboard /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties" element={<PrivateRoute><MainLayout><Properties /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId" element={<PrivateRoute><MainLayout><PropertyDetails /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/consultation-report/edit" element={<PrivateRoute><MainLayout><ConsultationReportEdit /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/consultation-report/preview" element={<PrivateRoute><MainLayout><ConsultationReportPreview /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/consultation-report/preview/embed" element={<PrivateRoute><ConsultationReportPreviewEmbed /></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/visitor-inspection-report" element={<PrivateRoute><MainLayout><VisitorInspectionReportEdit /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/open-schedule" element={<PrivateRoute><MainLayout><OpenSchedule /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/auction-scheduling" element={<PrivateRoute><MainLayout><AuctionSchedulingPage /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/contractor-jobs" element={<PrivateRoute><MainLayout><PropertyContractorJobsPage /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/:propertyId/rental-applications" element={<PrivateRoute><MainLayout><PropertyRentalApplications /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/properties/new" element={<PrivateRoute><MainLayout><NewProperty /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/customers" element={<PrivateRoute><MainLayout><Customers /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/vendors" element={<PrivateRoute requireAgencyAdmin={true}><MainLayout><Customers /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/attendees" element={<PrivateRoute><MainLayout><Attendees /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/calendar" element={<PrivateRoute><MainLayout><Calendar /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/workflow-templates" element={
              <PrivateRoute>
                <MainLayout>
                  <WorkflowTemplatesWrapper />
                </MainLayout>
              </PrivateRoute>
            } />
            <Route path="/agencies/:agencySlug/styling-companies" element={<PrivateRoute><MainLayout><StylingCompanies /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/photographers" element={<PrivateRoute><MainLayout><Photographers /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/contractors" element={<PrivateRoute><MainLayout><Contractors /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/team" element={<PrivateRoute><MainLayout><Team /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/reports" element={<PrivateRoute><MainLayout><Reports /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/settings" element={<PrivateRoute><MainLayout><Settings /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/commission-sharing-policy" element={<PrivateRoute requireAgencyAdmin={true}><MainLayout><CommissionSharingPolicy /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/commission-report" element={<PrivateRoute><MainLayout><CommissionReport /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/commission-performance" element={<PrivateRoute requireAgencyAdmin={true}><MainLayout><CommissionPerformance /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/cost-items" element={<PrivateRoute requireAgencyAdmin={true}><MainLayout><CostItems /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/third-party-integrations" element={<PrivateRoute><MainLayout><ThirdPartyIntegrations /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/integrations/realestate" element={<PrivateRoute><MainLayout><RealestateIntegration /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/integrations/domain" element={<PrivateRoute><MainLayout><DomainIntegration /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/integrations/domain/callback" element={<PrivateRoute><MainLayout><DomainIntegration /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/subscription" element={<PrivateRoute><MainLayout><ManageSubscription /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/market-campaign" element={<PrivateRoute><MainLayout><MarketCampaign /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/profile" element={<PrivateRoute><MainLayout><Profile /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/contact-sales" element={<PrivateRoute><MainLayout><ContactSales variant="agency" /></MainLayout></PrivateRoute>} />
            <Route path="/agencies/:agencySlug/support" element={<PrivateRoute><MainLayout><AgentSupport /></MainLayout></PrivateRoute>} />
            <Route path="/contact-sales" element={<ContactSales variant="public" />} />
            <Route path="/:agencySlug/rental-apply/:token" element={<RentalApply />} />
            <Route path="/rental-apply/view/:applicantToken" element={<RentalApplicationView />} />
            
            {/* Contractor job portal - show contractor layout if visiting job pages */}
            <Route path="/jobs/:token" element={
              <ContractorLayout>
                <Elements stripe={stripePromise}>
                  <ContractorJobPortal />
                </Elements>
              </ContractorLayout>
            } />
            <Route path="/jobs/invite/:token" element={
              <ContractorLayout>
                <Elements stripe={stripePromise}>
                  <ContractorJobPortal />
                </Elements>
              </ContractorLayout>
            } />
            <Route path="/jobs/retrieve/:jobId" element={
              <ContractorLayout>
                <Elements stripe={stripePromise}>
                  <ContractorRetrieveCode />
                </Elements>
              </ContractorLayout>
            } />
            <Route path="/contractor/job/:jobId/:retrieveCode" element={
              <ContractorLayout>
                <Elements stripe={stripePromise}>
                  <ContractorJobPortal />
                </Elements>
              </ContractorLayout>
            } />
            
            {/* Contractor portal with layout */}
            <Route path="/contractor/dashboard" element={<ContractorLayout><ContractorDashboard /></ContractorLayout>} />
            <Route path="/contractor/subscription" element={
              <ContractorLayout>
                <Elements stripe={stripePromise}>
                  <ContractorSubscriptionManagement />
                </Elements>
              </ContractorLayout>
            } />
            <Route path="/contractor/profile" element={<ContractorLayout><ContractorProfile /></ContractorLayout>} />
            
            {/* Contractor authentication */}
            <Route path="/contractor" element={<Navigate to="/contractor/auth" replace />} />
            <Route path="/contractor/" element={<Navigate to="/contractor/auth" replace />} />
            <Route path="/contractor/auth" element={<ContractorAuth />} />
            
            {/* Agency-specific login route - placed last to avoid conflicts with other routes */}
            <Route path="/:agencySlug" element={<AgencyLogin />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
